import { useMemo } from "react";
import type { PaygapMetricItemDto } from "@green0meter/api-client";

import type { ContributorItem } from "../PaygapPositionRoleOverview";

interface UseContributorDataProps {
  analysisResults: PaygapMetricItemDto[] | null | undefined;
  filterText: string;
}

interface UseContributorDataReturn {
  topContributorData: ContributorItem[];
  filteredContributorData: ContributorItem[];
}

/**
 * Hook to extract and filter top contributor data from analysis results
 */
export const useContributorData = ({
  analysisResults,
  filterText,
}: UseContributorDataProps): UseContributorDataReturn => {
  // Extract top-contributor data from analysisResults
  const topContributorData = useMemo((): ContributorItem[] => {
    if (!analysisResults || analysisResults.length === 0) return [];

    const contributors: ContributorItem[] = [];

    // Iterate through flat analysisResults array
    analysisResults.forEach((item) => {
      if (item.item && item.item.startsWith("top-contributor-")) {
        const keyWithoutPrefix = item.item.replace("top-contributor-", "");

        contributors.push({
          key: keyWithoutPrefix,
          value: item.value || "",
        });
      }
    });

    return contributors;
  }, [analysisResults]);

  // Filter top contributor data based on search text
  const filteredContributorData = useMemo((): ContributorItem[] => {
    if (!filterText) return topContributorData;
    const lowerFilter = filterText.toLowerCase();

    return topContributorData.filter(
      (item) =>
        item.key.toLowerCase().includes(lowerFilter) ||
        item.value.toLowerCase().includes(lowerFilter),
    );
  }, [topContributorData, filterText]);

  return {
    topContributorData,
    filteredContributorData,
  };
};
